import { forwardRef, useEffect } from 'react'

import styles from './Modal.module.css'

export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 是否显示 */
  open?: boolean
  /** 关闭回调 */
  onClose?: () => void
  /** 标题 */
  title?: string
  /** 内容 */
  children?: React.ReactNode
  /** 底部按钮区域 */
  footer?: React.ReactNode
  /** 点击遮罩是否关闭 */
  closeOnOverlayClick?: boolean
  /** 图标类型 */
  icon?: 'warning' | 'error' | 'success' | 'info'
  /** 是否居中布局（图标+标题+内容居中） */
  centered?: boolean
}

/** 图标组件 */
const IconComponent = ({ type }: { type: ModalProps['icon'] }) => {
  const iconMap = {
    warning: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path d="M24 14v14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <circle cx="24" cy="34" r="2" fill="currentColor" />
      </svg>
    ),
    error: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path
          d="M16 16l16 16M32 16L16 32"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    ),
    success: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path
          d="M15 24l6 6 12-12"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    info: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <circle cx="24" cy="14" r="2" fill="currentColor" />
        <path d="M24 20v16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    ),
  }

  return type ? iconMap[type] : null
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open = false,
      onClose,
      title,
      children,
      footer,
      closeOnOverlayClick = true,
      icon,
      centered = false,
      className,
      ...props
    },
    ref
  ) => {
    // 按 ESC 关闭
    useEffect(() => {
      if (!open) return

      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && onClose) {
          onClose()
        }
      }

      document.addEventListener('keydown', handleEsc)
      // 防止背景滚动
      document.body.style.overflow = 'hidden'

      return () => {
        document.removeEventListener('keydown', handleEsc)
        document.body.style.overflow = ''
      }
    }, [open, onClose])

    if (!open) {
      return null
    }

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (closeOnOverlayClick && e.target === e.currentTarget && onClose) {
        onClose()
      }
    }

    const classNames = [styles.modal, centered && styles.centered, className]
      .filter(Boolean)
      .join(' ')
    const iconClass = icon ? styles[`icon${icon.charAt(0).toUpperCase()}${icon.slice(1)}`] : ''

    return (
      <div className={styles.overlay} onClick={handleOverlayClick} role="dialog" aria-modal="true">
        <div ref={ref} className={classNames} {...props}>
          {/* 关闭按钮（居中模式下移到右上角） */}
          {centered && onClose && (
            <button
              className={styles.closeButtonAbsolute}
              onClick={onClose}
              aria-label="Close"
              data-ga-action-name="modal_close_click"
              data-ga-module="modal"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* 非居中模式的标准 header */}
          {!centered && title && (
            <div className={styles.header}>
              <h2 className={styles.title}>{title}</h2>
              {onClose && (
                <button
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label="Close"
                  data-ga-action-name="modal_close_click"
                  data-ga-module="modal"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* 居中模式的内容区域 */}
          {centered ? (
            <div className={styles.centeredContent}>
              {icon && (
                <div className={`${styles.iconWrapper} ${iconClass}`}>
                  <IconComponent type={icon} />
                </div>
              )}
              {title && <h2 className={styles.centeredTitle}>{title}</h2>}
              <div className={styles.centeredBody}>{children}</div>
            </div>
          ) : (
            <div className={styles.content}>{children}</div>
          )}

          {footer && (
            <div className={centered ? styles.centeredFooter : styles.footer}>{footer}</div>
          )}
        </div>
      </div>
    )
  }
)

Modal.displayName = 'Modal'
